import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import axios from 'axios';
import { getApiBaseUrl } from '../../../core/config/api';
import {
  getSeverityColor,
  getSeverityIcon,
  getTypeLabel,
  getTypeIcon,
} from './hooks/securityLogsHelpers';
import SecurityLogsStatsCards from './hooks/SecurityLogsStatsCards';
import SecurityLogsFiltersPanel from './hooks/SecurityLogsFiltersPanel';

const securityLogsApiBase = (): string => getApiBaseUrl(true).replace(/\/$/, '');

interface SecurityLog {
  id: number;
  type: string;
  user_id: number | null;
  username: string | null;
  ip_address: string;
  user_agent: string | null;
  path: string;
  method: string;
  details: string | null;
  severity: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
}

interface SecurityStats {
  total: number;
  byType: Array<{ type: string; count: number }>;
  bySeverity: Array<{ severity: string; count: number }>;
  topIPs: Array<{ ip_address: string; count: number }>;
}

const SecurityLogs: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const isEmbedded = embedded || new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  ).get('embed') === '1';

  const [logs, setLogs] = useState<SecurityLog[]>([]);
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showSeverityLegend, setShowSeverityLegend] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    user_id: '',
    ip_address: '',
    start_date: '',
    end_date: ''
  });

  // Vérifier les permissions
  const hasAccess = currentUser?.roles_id && currentUser.roles_id <= 3; // CM, Admin, Modo

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    if (!hasAccess) {
      navigate(isEmbedded ? '/panel/dashboard' : '/panel');
      return;
    }

    loadLogs();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, page, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });

      const response = await axios.get(
        `${securityLogsApiBase()}/security-logs?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setLogs(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des logs:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${securityLogsApiBase()}/security-logs/stats?days=7`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des statistiques:', err);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset à la page 1 lors d'un changement de filtre
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      severity: '',
      user_id: '',
      ip_address: '',
      start_date: '',
      end_date: ''
    });
    setPage(1);
  };

  // Helpers de présentation extraits dans hooks/securityLogsHelpers.tsx


  if (!currentUser || !hasAccess) {
    return null;
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const content = (
    <>
          {/* Header avec bouton retour (standalone uniquement) */}
          {!isEmbedded && (
          <div className="mb-8 flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="bg-red-500/20 p-3 rounded-xl backdrop-blur-sm border border-red-500/30">
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                    Logs de Sécurité
                  </h1>
                  <p className="text-gray-300 text-lg">
                    Surveillance et analyse des événements de sécurité en temps réel
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2 backdrop-blur-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour au panel</span>
            </button>
          </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}

          {/* Statistiques (extraites dans hooks/SecurityLogsStatsCards.tsx) */}
          {stats && <SecurityLogsStatsCards stats={stats} />}

          {/* Filtres avec accordéon */}
          <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-4 backdrop-blur-sm mb-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                <svg className="w-4 h-4 text-[#7b93f5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtres de recherche
                {hasActiveFilters && (
                  <span className="px-2 py-0.5 bg-[#3d4fdb]/20 text-[#7b93f5] text-xs rounded-full border border-[#3d4fdb]/30">
                    Actifs
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="rounded-lg border border-white/15 bg-black/30 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-black/45 transition-colors"
                  >
                    Réinitialiser
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-3 py-1.5 text-xs font-medium text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors flex items-center gap-1.5"
                >
                  <svg className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  {showFilters ? 'Masquer' : 'Afficher'}
                </button>
              </div>
            </div>

            {showFilters && (
              <SecurityLogsFiltersPanel filters={filters} onChange={handleFilterChange} />
            )}
          </div>

          {/* Table des logs */}
          <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 backdrop-blur-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-[#1e2d4d] flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                <svg className="w-4 h-4 text-[#7b93f5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Événements de sécurité
              </h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowSeverityLegend((prev) => !prev)}
                    className="h-7 w-7 rounded-full border border-[#2a3a62] bg-[#0a132b] text-slate-300 hover:bg-[#0d1b3a] transition-colors text-xs"
                    aria-label="Afficher la légende des niveaux de sévérité"
                    title="Légende des niveaux"
                  >
                    ?
                  </button>
                  {showSeverityLegend && (
                    <div className="absolute right-0 top-9 z-20 w-72 rounded-lg border border-[#243052]/90 bg-[#0a1128] p-3 shadow-xl">
                      <p className="text-xs font-semibold text-white mb-2">Légende des niveaux</p>
                      <ul className="space-y-1.5 text-xs text-slate-400">
                        <li><span className="text-red-400 font-semibold">Critique</span> : action immédiate, risque fort.</li>
                        <li><span className="text-orange-400 font-semibold">Élevée</span> : traitement prioritaire dans la journée.</li>
                        <li><span className="text-yellow-400 font-semibold">Moyenne</span> : à investiguer rapidement, impact modéré.</li>
                        <li><span className="text-emerald-400 font-semibold">Faible</span> : information/veille, impact limité.</li>
                      </ul>
                    </div>
                  )}
                </div>
                <span className="text-xs text-slate-400">
                  {logs.length} événement{logs.length > 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <LoadingSpinner />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-10 h-10 mx-auto mb-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-slate-400">Aucun log trouvé</p>
                <p className="text-slate-500 text-sm mt-1">Essayez de modifier vos filtres de recherche</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1e2d4d] bg-[#0d1b3a]/50">
                        <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Date & Heure</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Type</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Sévérité</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Utilisateur</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">IP</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Endpoint</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Détails</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1e2d4d]/60">
                      {logs.map((log) => (
                        <tr
                          key={log.id}
                          className="hover:bg-[#0d1b3a]/40 transition-colors group"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-xs text-slate-300 font-medium">
                              {new Date(log.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-slate-500">
                              {new Date(log.created_at).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span>{getTypeIcon(log.type)}</span>
                              <span className="text-xs text-slate-300">{getTypeLabel(log.type)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(log.severity)}`}>
                              {getSeverityIcon(log.severity)}
                              <span>{log.severity.toUpperCase()}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {log.username ? (
                              <div className="flex items-center gap-1.5">
                                <div className="w-6 h-6 bg-gradient-to-br from-[#3d4fdb] to-violet-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  {log.username.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-xs text-slate-300">{log.username}</span>
                              </div>
                            ) : log.user_id ? (
                              <span className="text-xs text-slate-400">ID: {log.user_id}</span>
                            ) : (
                              <span className="text-xs text-slate-500 italic">Anonyme</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-1 rounded border border-[#2b3a61] bg-[#0d1b3a]/80 text-xs font-mono text-slate-300">
                              {log.ip_address}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                log.method === 'GET' ? 'bg-emerald-500/20 text-emerald-400' :
                                log.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                                log.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                                log.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {log.method}
                              </span>
                              <span className="text-xs text-slate-400 font-mono truncate max-w-xs">
                                {log.path}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs text-slate-400 max-w-sm truncate" title={log.details || ''}>
                              {log.details || <span className="text-slate-600 italic">Aucun détail</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-[#1e2d4d] flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      Page <span className="text-slate-200">{page}</span> / <span className="text-slate-200">{totalPages}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-1.5 text-xs text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0d1b3a] transition-colors"
                      >
                        Précédent
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`rounded-lg px-3 py-1.5 text-xs transition-colors ${
                              page === pageNum
                                ? 'bg-[#3d4fdb]/20 border border-[#3d4fdb]/40 text-[#8ea0f5]'
                                : 'border border-[#2a3a62] bg-[#0a132b] text-slate-400 hover:bg-[#0d1b3a]'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-1.5 text-xs text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0d1b3a] transition-colors"
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
    </>
  );

  if (isEmbedded) {
    return <div className="space-y-4">{content}</div>;
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {content}
        </div>
      </div>
    </div>
  );
};

export default SecurityLogs;
